import React, { useState, useEffect, useContext, useCallback } from 'react';
import { PresenceContext } from '../App';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import './RadioPlayer.css';
import defaultAlbumImage from '../assets/am group.png';
import { createConnectionLog, updateConnectionLog } from '../services/firebase';

const audioStream = new Audio();

const RadioPlayer = ({ serverId }) => {
  const { 
    handleNameSubmit, 
    handleRadioChange, 
    registerInFirebase, 
    updateOnlineStatus
  } = useContext(PresenceContext);
  
  const [djData, setDjData] = useState(null);
  const [songData, setSongData] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [streamUrl, setStreamUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [currentConnectionId, setCurrentConnectionId] = useState(null);

  // Verificar que las funciones existan
  useEffect(() => {
    console.log('Funciones disponibles:', {
      handleNameSubmit: !!handleNameSubmit,
      handleRadioChange: !!handleRadioChange,
      registerInFirebase: !!registerInFirebase,
      updateOnlineStatus: !!updateOnlineStatus
    });
  }, [handleNameSubmit, handleRadioChange, registerInFirebase, updateOnlineStatus]);

  // Función para detener el reproductor
  const stopPlayer = useCallback(async () => {
    if (isPlaying) {
      audioStream.pause();
      const userName = JSON.parse(localStorage.getItem('user'))?.name;
      const radioPath = window.location.pathname.split('/')[1];
      if (userName && radioPath) {
        if (currentConnectionId) {
          try {
            const now = new Date().getTime();
            await updateConnectionLog(radioPath, currentConnectionId, {
              endTime: now,
              status: 'ended',
              disconnectReason: 'stop_player'
            });
            console.log('Conexión finalizada en Firestore:', currentConnectionId);
          } catch (error) {
            console.error('Error actualizando registro de conexión:', error);
          }
        }
        await updateOnlineStatus(userName, radioPath, false);
      }
      setIsPlaying(false);
      setCurrentConnectionId(null);
    }
  }, [isPlaying, updateOnlineStatus, currentConnectionId]);

  // Exponer la función de detención al contexto global
  useEffect(() => {
    window.stopRadioPlayer = stopPlayer;
    return () => {
      delete window.stopRadioPlayer;
    };
  }, [stopPlayer]);

  // Función para obtener la información del stream y la canción actual
  const fetchData = async () => {
    setIsLoading(true); // Activar indicador de carga
    try {
      const [djResponse, songResponse, channelResponse] = await Promise.all([
        axios.get(`https://radio.amgroupmkt.com:1030/api/v2/djs/?server=${serverId}`),
        axios.get(`https://radio.amgroupmkt.com:1030/api/v2/history/?limit=1&offset=0&server=${serverId}`),
        axios.get(`https://radio.amgroupmkt.com:1030/api/v2/channels/?server=${serverId}`)
      ]);

      const djData = djResponse.data[0];
      const songData = songResponse.data.results[0];
      const channelData = channelResponse.data[0]?.secure_stream_url;

      setDjData(djData);
      setSongData(songData);
      setStreamUrl(channelData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false); // Desactivar indicador de carga
    }
  };

  useEffect(() => {
    fetchData();
    // Actualizar el stream cada 30 segundos
    const intervalId = setInterval(fetchData, 15000);
    return () => clearInterval(intervalId);
  }, [serverId]);


  // Agregar efecto para verificar estado online periódicamente
  useEffect(() => {
    let intervalId;

    if (isPlaying) {
      console.log('🔄 Iniciando verificación periódica del estado online...');
      
      intervalId = setInterval(async () => {
        const userString = localStorage.getItem('user');
        const userData = userString ? JSON.parse(userString) : null;
        const userName = userData?.name || 'Usuario Externo';
        const currentPath = window.location.pathname;
        const radioPath = currentPath.split('/')[1];

        if (userName && radioPath) {
          console.log('📡 Verificando estado online...');
          await updateOnlineStatus(userName, radioPath, true);
        }
      }, 5000); // Verificar cada 5 segundos
    }

    return () => {
      if (intervalId) {
        console.log('🛑 Deteniendo verificación periódica');
        clearInterval(intervalId);
      }
    };
  }, [isPlaying, updateOnlineStatus]);

  const togglePlay = async () => {
    if (!isLoading) {
      setIsPlaying(!isPlaying);
      if (!isPlaying) {
        setIsLoading(true);
        audioStream.src = '';
        audioStream.load();
        audioStream.src = streamUrl;
        
        try {
          await audioStream.play();
          setIsLoading(false);
          
          const userString = localStorage.getItem('user');
          const userData = userString ? JSON.parse(userString) : null;
          const userName = userData?.name || 'Usuario Externo';
          const currentPath = window.location.pathname;
          const radioPath = currentPath.split('/')[1];

          let ipAddress = '';
          try {
            // Primer intento con ipify
            const ipResponse = await fetch('https://api.ipify.org?format=json');
            if (!ipResponse.ok) throw new Error('Falló ipify');
            const ipData = await ipResponse.json();
            ipAddress = ipData.ip;
            console.log('IP obtenida de ipify:', ipAddress);
          } catch (error) {
            console.error('Error con ipify, intentando con ipapi:', error);
            try {
              // Segundo intento con ipapi
              const ipapiResponse = await fetch('https://ipapi.co/json/');
              if (!ipapiResponse.ok) throw new Error('Falló ipapi');
              const ipapiData = await ipapiResponse.json();
              ipAddress = ipapiData.ip;
              console.log('IP obtenida de ipapi:', ipAddress);
            } catch (error2) {
              console.error('Error obteniendo IP con ambos servicios:', error2);
            }
          }

          if (!ipAddress) {
            console.error('⚠️ No se pudo obtener la IP del usuario');
            return; // No permitir la conexión sin IP
          }

          // Primero obtener los datos de geolocalización
          let geoipData = null;
          try {
            const geoipResponse = await axios.get(`https://radio.amgroupmkt.com:1030/api/v2/listeners_geoip/?server=${serverId}`);
            geoipData = geoipResponse.data.find(user => user.ip === ipAddress)?.geodata;
          } catch (error) {
            console.error('Error obteniendo datos de geolocalización:', error);
          }

          const now = new Date().getTime();
          const connectionLog = await createConnectionLog(radioPath, {
            clientName: userName,
            ip: ipAddress,
            userAgent: navigator.userAgent,
            streamId: serverId,
            playerType: 'web',
            status: 'active',
            connectionStartTime: now,
            geodata: geoipData ? {
              city: geoipData.city || 'Desconocido',
              region: geoipData.region || '',
              country: geoipData.country_name || '',
              latitude: geoipData.latitude,
              longitude: geoipData.longitude
            } : null,
            metadata: {
              browser: navigator.userAgent,
              platform: navigator.platform,
              language: navigator.language,
              screenResolution: `${window.screen.width}x${window.screen.height}`,
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              ipAddress: ipAddress
            }
          });
          
          console.log('Conexión registrada en Firestore:', connectionLog.id);
          setCurrentConnectionId(connectionLog.id);

          await registerInFirebase(userName, radioPath);
          await updateOnlineStatus(userName, radioPath, true);
          
        } catch (error) {
          console.error('Error reproduciendo stream:', error);
          setIsLoading(false);
          const userName = JSON.parse(localStorage.getItem('user'))?.name;
          const radioPath = window.location.pathname.split('/')[1];
          await updateOnlineStatus(userName, radioPath, false);
        }
      } else {
        audioStream.pause();
        audioStream.src = '';
        audioStream.load();
        const userName = JSON.parse(localStorage.getItem('user'))?.name;
        const radioPath = window.location.pathname.split('/')[1];
        
        // Registrar fin de conexión en Firestore
        if (currentConnectionId) {
          try {
            await updateConnectionLog(radioPath, currentConnectionId, {
              endTime: new Date().getTime(),
              status: 'ended',
              disconnectReason: 'manual_stop'
            });
            console.log('Conexión finalizada en Firestore:', currentConnectionId);
          } catch (error) {
            console.error('Error actualizando registro de conexión:', error);
          }
        }

        await updateOnlineStatus(userName, radioPath, false);
        setCurrentConnectionId(null);
      }
    }
  };

  // Modificar el useEffect para manejar async/await
  useEffect(() => {
    return () => {
      if (isPlaying && currentConnectionId) {
        const userName = JSON.parse(localStorage.getItem('user'))?.name;
        const radioPath = window.location.pathname.split('/')[1];
        
        console.log('Limpiando stream por desmontaje o recarga...');
        audioStream.pause();
        audioStream.src = '';
        audioStream.load();
        
        // Usar una función async inmediatamente invocada
        (async () => {
          try {
            const now = new Date().getTime();
            await updateConnectionLog(radioPath, currentConnectionId, {
              endTime: now,
              status: 'ended',
              disconnectReason: 'page_reload_or_unmount'
            });
            console.log('Conexión finalizada en Firestore por desmontaje/recarga:', currentConnectionId);
            await updateOnlineStatus(userName, radioPath, false);
          } catch (error) {
            console.error('Error actualizando registro de conexión:', error);
          }
        })();
      }
    };
  }, [isPlaying, currentConnectionId, updateOnlineStatus]);

  // Agregar un manejador para el evento beforeunload
  useEffect(() => {
    const handleBeforeUnload = async (event) => {
      if (isPlaying && currentConnectionId) {
        const userName = JSON.parse(localStorage.getItem('user'))?.name;
        const radioPath = window.location.pathname.split('/')[1];
        
        try {
          const now = new Date().getTime();
          await updateConnectionLog(radioPath, currentConnectionId, {
            endTime: now,
            status: 'ended',
            disconnectReason: 'page_reload'
          });
          await updateOnlineStatus(userName, radioPath, false);
        } catch (error) {
          console.error('Error actualizando registro de conexión:', error);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isPlaying, currentConnectionId, updateOnlineStatus]);

  return (
    <div className="container-fluid radio-player">
      <div className="player-content">
        <div className="play-control" onClick={togglePlay}>
          {isLoading ? (
            <div className="loading-indicator active" />
          ) : (
            <div className="play-btn-wrapper">
              <button className={`play-btn ${isPlaying ? 'playing' : ''}`}>
                <svg viewBox="0 0 24 24">
                  {isPlaying ? (
                    <>
                      <rect x="6" y="4" width="4" height="16" rx="1" />
                      <rect x="14" y="4" width="4" height="16" rx="1" />
                    </>
                  ) : (
                    <path d="M8 5v14l11-7z" />
                  )}
                </svg>
              </button>
            </div>
          )}
        </div>

        <div className="track-info">
          <div className="album-art">
            <img src={songData?.img_url || defaultAlbumImage} alt="Album" />
          </div>
          <div className="metadata">
            <h5>{songData?.metadata}</h5>
            <div className="visualizer-backdrop">
              <div className="bars-container">
                {[...Array(50)].map((_, i) => (
                  <div 
                    key={i} 
                    className={`bar ${isPlaying ? 'animated' : ''}`}
                    style={{
                      animationDelay: `${i * 0.05}s`,
                      height: isPlaying ? undefined : '2px'
                    }}
                  />
                ))}
              </div>
              {isPlaying && (
                <div className="music-particles">
                  {[...Array(10)].map((_, i) => (
                    <div 
                      key={i} 
                      className="particle"
                      style={{
                        animationDelay: `${i * 0.3}s`
                      }}
                    >
                      ♪
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RadioPlayer;
